import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReCAPTCHA from "react-google-recaptcha";
import { sendOtp, verifyOtp } from "../slices/authSlice";
import { motion } from "framer-motion";
import { toast } from "react-toastify";

const RegisterModal = ({ onClose, onSwitchToLogin }) => {
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.auth);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  const [recaptcha, setRecaptcha] = useState(null);
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(0);
  const [disableFields, setDisableFields] = useState(false);
  const [showOtpForm, setShowOtpForm] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(true);

  // 🕒 Timer countdown
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => setTimer((t) => t - 1), 1000);
    } else {
      setResendDisabled(false);
    }
    return () => clearInterval(interval);
  }, [timer]);

  // 📩 Send OTP or Resend OTP
  const handleSendOtp = (e) => {
    if (e) e.preventDefault();

    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill all required fields");
      return;
    }

    // ⚡ Password length validation
    if (form.password.length < 6) {
      toast.error("Password must be of at least six characters");
      return;
    }


     if (form.phone.length < 10) {
      toast.error("Phone Number must be of 10 digit");
      return;
    }

    if (form.password !== form.confirmPassword) {
      toast.error("Passwords do not match!");
      return;
    }

    // Prevent resend while timer active
    if (resendDisabled && timer > 0) {
      toast.info(`Please wait ${timer}s before requesting OTP again.`);
      return;
    }

    setResendDisabled(true);
    setTimer(60);

    dispatch(sendOtp({ email: form.email })).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        toast.success("OTP sent to your email!");
        setDisableFields(true);
        setShowOtpForm(true);
      } else {
        toast.error("Failed to send OTP. Please try again later.");
        setResendDisabled(false);
        setTimer(0);
      }
    });
  };

  // ✅ Verify OTP
  const handleVerifyOtp = (e) => {
    e.preventDefault();

    if (!otp) {
      toast.error("Please enter OTP");
      return;
    }

    dispatch(
      verifyOtp({
        name: form.name,
        email: form.email,
        phone: form.phone,
        password: form.password,
        otp,
      })
    ).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        toast.success("Registration successful!");
        onClose();

        setTimeout(() => {
          window.location.reload();
        }, 800);
      } else {
        toast.error("Invalid OTP or registration failed.");
      }
    });
  };

  // ↩️ Back button
  const handleBackToRegister = () => {
    setShowOtpForm(false);
    setOtp("");
    setDisableFields(false);
    setTimer(0);
    setResendDisabled(true);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center " style={{zIndex:10000}}>
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 30 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ type: "spring", duration: 0.4 }}
        className="bg-white/90 backdrop-blur-md rounded-2xl shadow-2xl p-6 w-full max-w-md border border-gray-200"
      >
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-4">
          {showOtpForm ? "Verify OTP" : "Create Account"}
        </h2>

        {error && (
          <p className="text-red-500 text-sm text-center mb-2">{error}</p>
        )}

        {!showOtpForm ? (
          <form onSubmit={handleSendOtp} className="space-y-4">
            {/* Name */}
            <input
              type="text"
              placeholder="Full Name"
              className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-black/70 outline-none"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              disabled={disableFields}
            />

            {/* Email */}
            <input
              type="email"
              placeholder="Email Address"
              className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-black/70 outline-none"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              disabled={disableFields}
            />

            {/* Phone */}
            <input
              type="tel"
              placeholder="Phone Number"
              className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-black/70 outline-none"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              required
              disabled={disableFields}
            />

            {/* Password */}
            <input
              type="password"
              placeholder="Password (min 6 characters)"
              className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-black/70 outline-none"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              disabled={disableFields}
            />

            {/* Confirm Password */}
            <input
              type="password"
              placeholder="Confirm Password"
              className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-black/70 outline-none"
              value={form.confirmPassword}
              onChange={(e) =>
                setForm({ ...form, confirmPassword: e.target.value })
              }
              required
              disabled={disableFields}
            />

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-black text-white py-3 rounded-xl hover:bg-gray-800 transition-all"
            >
              {loading ? "Sending OTP..." : "Send OTP"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp} className="space-y-4">
            <input
              type="text"
              placeholder="Enter OTP"
              className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-black/70 outline-none"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              required
            />

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-green-600 text-white py-3 rounded-xl hover:bg-green-700 transition-all"
            >
              {loading ? "Verifying..." : "Verify OTP & Register"}
            </button>

            <div className="flex justify-between items-center text-sm mt-2">
              <button
                type="button"
                onClick={handleBackToRegister}
                className="text-gray-600 hover:text-black transition"
              >
                ← Back
              </button>

              {resendDisabled ? (
                <span className="text-gray-400">Resend in {timer}s</span>
              ) : (
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={resendDisabled}
                  className={`${
                    resendDisabled
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-black font-medium hover:underline"
                  }`}
                >
                  Resend OTP
                </button>
              )}
            </div>
          </form>
        )}

        <p className="mt-4 text-sm text-center text-gray-600">
          Already have an account?{" "}
          <span
            onClick={onSwitchToLogin}
            className="text-black font-semibold cursor-pointer hover:underline"
          >
            Login
          </span>
        </p>

        <button
          onClick={onClose}
          className="mt-5 w-full py-2.5 rounded-xl bg-gray-200 hover:bg-gray-300 transition-all"
        >
          Close
        </button>
      </motion.div>
    </div>
  );
};

export default RegisterModal;
